/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import { cls } from "cls";
import { Icon } from "@clarkinc/zest-design-system";
import { VariationGroup, VariationGroupItem, VariationMembership } from "./ProductDetailPageResult";
import { selectedTileButtonBorderStyling, baseHoverStyling, baseBorderStyling } from "./VirtualGrouping/VirtualGroupingTiles.styles";
import QuantityVariationItem from "./QuantityVariationItem";
import ThumbnailVariation from "./ThumbnailVariation";
import variationAnalyticsClickTracking from "../analytics/PdpPageTracking/variationAnalyticsClickTracking";
interface ProductVariationsProps {
  itemNumber: string;
  variationMembership?: VariationMembership;
  isVirtualGroupMaster: boolean;
}
const variationHeading = (variationGroup: VariationGroup, itemNumber: string) => {
  if (variationGroup.optionName !== "Quantity") {
    const selectedItem = variationGroup.variationGroupItems.find(item => {
      return item.itemNumber === itemNumber;
    });
    return <h2 data-testid="productVariationsHeading" id="productVariationsHeading" className="mb-1 text-sm">
        {`${variationGroup.optionName}: `}
        <span className="font-normal">{selectedItem?.variationText}</span>
      </h2>;
  }
  return <h2 data-testid="productVariationsHeading" id="productVariationsHeading" className="mb-1 text-sm" data-sentry-component="variationHeading" data-sentry-source-file="ProductVariations.tsx">
      {variationGroup.optionName === "Quantity" ? "Available Quantities:" : `${variationGroup.displayText || "Other Available"}: `}
    </h2>;
};
const ProductVariations = ({
  itemNumber,
  variationMembership,
  isVirtualGroupMaster
}: ProductVariationsProps) => {
  const lowerCaseItemNumber = itemNumber.toLowerCase();
  const sortGroupItems = (group: VariationGroup, a: VariationGroupItem, b: VariationGroupItem) => {
    if (group.isSorted) {
      if (a.itemOrderBy < b.itemOrderBy) {
        return -1;
      }
      if (a.itemOrderBy > b.itemOrderBy) {
        return 1;
      }
      return 0;
    }
    return a.displayText.toLowerCase().localeCompare(b.displayText.toLowerCase());
  };
  const getHrefTarget = (groupItem: VariationGroupItem, isSelected: boolean) => {
    if (isSelected) {
      return "#";
    }
    if (isVirtualGroupMaster) {
      return groupItem.groupingMasterLink ?? groupItem.seoLink;
    }
    return groupItem.seoLink;
  };
  const getTextClass = (isSelected: boolean, isInStock: boolean) => {
    if (isSelected) {
      return "text-white font-semibold";
    }
    if (isInStock) {
      return "text-green-500 font-semibold";
    }
    return "text-gray-600 font-normal var-out-of-stock";
  };
  const getListClass = (group: VariationGroup) => {
    if (group.optionName === "Quantity") {
      return "grid grid-flow-row grid-cols-3 gap-2";
    }
    return "nav nav-pills nav-stacked";
  };
  const getNonThumbnailVariationItem = (groupItem: VariationGroupItem, isAnyItemInGroupPlusEligible: boolean, isSelected: boolean, isQuantity: boolean) => <>
      {isQuantity ? <QuantityVariationItem groupItem={groupItem} isAnyItemInGroupPlusEligible={isAnyItemInGroupPlusEligible} isSelected={isSelected} href={getHrefTarget(groupItem, isSelected)} itemNumber={lowerCaseItemNumber} /> : <li className={`${isSelected ? "var-selected" : ""} text-sm mb-0 pt-1`} key={groupItem.itemNumber}>
          <a href={getHrefTarget(groupItem, isSelected)} onClick={() => {
        variationAnalyticsClickTracking(groupItem.seoLink, groupItem.variationText);
      }} aria-current={isSelected ? "page" : "false"} className={`${getTextClass(isSelected, groupItem.isInStock)} leading-4 p-2 hover:underline`}>
            {groupItem.variationText}
            {!groupItem.isInStock && !isSelected && <span className="float-right text-gray-600 font-normal" data-testid="variationItemOutOfStock">
                Out of stock
              </span>}
            {isSelected && <Icon name="ri-check-line" theme="wss" className="fill-white w-6 h-6 float-right relative -top-1" />}
          </a>
        </li>}
    </>;
  const isQuantityGroup = (group: VariationGroup) => {
    return group.optionName === "Quantity";
  };
  const hasAnyPlusEligibleQuantities = (group: VariationGroup) => {
    if (!isQuantityGroup(group)) {
      return false;
    }
    return group.variationGroupItems?.some(item => item.isWebstaurantPlusEligible) ?? false;
  };
  const shouldDisplayAsTile = (group: VariationGroup) => group.displayTypeId === 1 && !group.variationGroupItems.some(item => item.variationText.length >= 18);
  return <>
      {variationMembership?.variationGroups.map(group => {
      let {
        displayTypeId
      } = group;
      if (shouldDisplayAsTile(group)) {
        displayTypeId = 4;
      }
      return <div id="productVariationBlock" key={`${group.id ?? "0"}.${group.optionName}`}>
            {displayTypeId !== 2 && displayTypeId !== 3 && displayTypeId !== 4 || isQuantityGroup(group) ? <>
                {variationHeading(group, lowerCaseItemNumber)}
                <div id="ProductVariations" data-testid="ProductVariations" className={`${!isQuantityGroup(group) ? "variation-list" : ""}`}>
                  <ul className={cls("list-none", "m-0", getListClass(group))} aria-labelledby="productVariationsHeading" data-testid={isQuantityGroup(group) ? "available-quantities" : "variation-ul"}>
                    {group.variationGroupItems.filter(groupItem => groupItem.itemNumber === lowerCaseItemNumber).map(groupItem => getNonThumbnailVariationItem(groupItem, hasAnyPlusEligibleQuantities(group), true, isQuantityGroup(group)))}
                    {group.variationGroupItems.filter(groupItem => groupItem.itemNumber !== lowerCaseItemNumber).sort((a, b) => sortGroupItems(group, a, b)).map(groupItem => getNonThumbnailVariationItem(groupItem, hasAnyPlusEligibleQuantities(group), false, isQuantityGroup(group)))}
                  </ul>
                </div>
              </> : <ThumbnailVariation key={`${group.id ?? "0"}.${group.optionName}`} group={group} lowerCaseItemNumber={lowerCaseItemNumber} getHrefTarget={getHrefTarget} baseBorderStyling={baseBorderStyling} selectedTileButtonBorderStyling={selectedTileButtonBorderStyling} baseHoverStyling={baseHoverStyling} sortGroupItems={sortGroupItems} displayTypeId={displayTypeId} />}
          </div>;
    })}
    </>;
};
export default ProductVariations;